import React from 'react';
import { Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import {
  setupTaxConfiguration as setupTaxConfigurationClevertap,
  confirmTaxConfiguration as confirmTaxConfigurationClevertap,
} from 'clevertap/settings/checkout';
import { useMokaOutletPropsFirst } from 'utils/formatter';
import styles from './TaxConfigurationModal.css';
import AssignedOutletsSection from './AssignedOutletsSection';

const BEFORE_DISCOUNT = 'BEFORE_DISCOUNT';
const AFTER_DISCOUNT = 'AFTER_DISCOUNT';

function sortOutlets(outlets) {
  return outlets.sort((a, b) =>
    a.outlet_name.localeCompare(b.outlet_name, 'en', { sensitivity: 'base' })
  );
}

class TaxConfigurationModal extends React.Component {
  state = {
    outletsBefore: [], // outlets with tax applied before discount
    outletsAfter: [], // outlets with tax applied after discount
    selectedOutletsBefore: [], // selected outletsBefore to be assigned to tax configuration after discount
    selectedOutletsAfter: [], // selected outletsAfter to be assigned to tax configuration before discount
    nameFilterBefore: '',
    nameFilterAfter: '',
    lastClickedOutletIndexBefore: undefined,
    lastClickedOutletIndexAfter: undefined,
  };

  getSettingOutletsWithName = (settingOutlets, outlets) => {
    const settingOutletsWithName = settingOutlets.map(settingOutlet => {
      const outlet = outlets.find(o => o.id === settingOutlet.outlet_id);
      if (outlet) {
        return {
          outlet_name: useMokaOutletPropsFirst(outlet, o => o.name),
          ...settingOutlet,
        };
      }
    });
    return settingOutletsWithName;
  };

  componentDidMount() {
    const { settingOutlets, outlets } = this.props;
    // Adding outlet name to setting outlets for easier manipulation
    const settingOutletsWithName = this.getSettingOutletsWithName(
      settingOutlets,
      outlets
    );

    const settingOutletsBefore = settingOutletsWithName.filter(
      outlet => outlet.tax_type === BEFORE_DISCOUNT
    );

    const settingOutletsAfter = settingOutletsWithName.filter(
      outlet => outlet.tax_type === AFTER_DISCOUNT
    );

    this.setState({
      outletsBefore: sortOutlets(settingOutletsBefore),
      outletsAfter: sortOutlets(settingOutletsAfter),
    });
  }

  render() {
    const {
      outletsBefore,
      outletsAfter,
      selectedOutletsBefore,
      selectedOutletsAfter,
      nameFilterBefore,
      nameFilterAfter,
    } = this.state;

    const { settingOutlets, isShowAnimation, outlets } = this.props;

    const settingOutletsWithName = this.getSettingOutletsWithName(
      settingOutlets,
      outlets
    );
    const settingOutletsBefore = settingOutletsWithName.filter(
      outlet => outlet.tax_type === BEFORE_DISCOUNT
    );

    const arrowRightDisabled =
      outletsBefore.length === 0 || selectedOutletsBefore.length === 0;
    const arrowLeftDisabled =
      outletsAfter.length === 0 || selectedOutletsAfter.length === 0;
    const confirmButtonDisabled = isEqual(
      outletsBefore,
      sortOutlets(settingOutletsBefore)
    );

    return (
      <Modal
        show
        animation={isShowAnimation}
        dialogClassName="taxcon-ftue-modal"
      >
        <Modal.Header>
          <Modal.Title className={styles.title}>
            Outlet Tax-Discount Preferences
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className={styles.body}>
          <div className={styles.outletSectionContainer}>
            <div className={styles.outletSection}>
              <div className={styles.titleSection}>
                Tax Applied Before Discount
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement="top"
                  overlay={
                    <Popover id="popoverBefore">
                      Tax is applied before the discount deduction. This
                      regulation is used in FnB restaurants in Jakarta and
                      several other areas
                    </Popover>
                  }
                >
                  <i className={classNames('fa fa-info', styles.infoIcon)} />
                </OverlayTrigger>
              </div>
              <AssignedOutletsSection
                assignedOutlets={outletsBefore}
                selectedOutlets={selectedOutletsBefore}
                onClickOutlet={this.handleClickOutletBefore}
                onNameFilterChange={this.handleNameFilterBeforeChange}
                nameFilter={nameFilterBefore}
              />
            </div>

            <div className={styles.arrowSection}>
              <i
                className={classNames('fa fa-caret-right', styles.arrow, {
                  [styles.disabled]: arrowRightDisabled,
                })}
                onClick={this.handleArrowRightClick}
              />
              <br />
              <i
                className={classNames('fa fa-caret-left', styles.arrow, {
                  [styles.disabled]: arrowLeftDisabled,
                })}
                onClick={this.handleArrowLeftClick}
              />
            </div>

            <div className={styles.outletSection}>
              <div className={styles.titleSection}>
                Tax Applied After Discount
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement="top"
                  overlay={
                    <Popover id="popoverAfter">
                      Tax is applied after the discount deduction. This
                      regulation is used in FnB restaurants in most areas in
                      Indonesia
                    </Popover>
                  }
                >
                  <i className={classNames('fa fa-info', styles.infoIcon)} />
                </OverlayTrigger>
              </div>
              <AssignedOutletsSection
                assignedOutlets={outletsAfter}
                selectedOutlets={selectedOutletsAfter}
                onClickOutlet={this.handleClickOutletAfter}
                onNameFilterChange={this.handleNameFilterAfterChange}
                nameFilter={nameFilterAfter}
              />
            </div>
          </div>

          <p className={styles.information}>
            Press Ctrl or Shift while clicking to select multiple outlets at
            once.
            <a
              href="http://blog.mokapos.com/cara-menghitung-dpp-pajak-restoran"
              target="_blank"
            >
              Click here
            </a>{' '}
            for more information about Tax-Discount Regulation
          </p>
        </Modal.Body>

        <Modal.Footer>
          <div className={styles.footer}>
            <button
              className={classNames('btn btn-default', styles.buttonCancel)}
              onClick={this.handleCancelButtonClick}
            >
              Cancel
            </button>
            <button
              className={classNames(
                'btn btn-primary',
                styles.buttonConfirm,
                'taxcon-ftue-confirm-button'
              )}
              onClick={this.handleConfirmButtonClick}
              disabled={confirmButtonDisabled}
            >
              Confirm
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }

  handleCancelButtonClick = () => {
    const { onCancel } = this.props;
    onCancel();
  };

  handleConfirmButtonClick = () => {
    const { onConfirm, settingOutlets } = this.props;
    const { outletsBefore, outletsAfter } = this.state;

    const settingOutletsBefore = settingOutlets.filter(
      outlet => outlet.tax_type === BEFORE_DISCOUNT
    );

    const settingOutletsAfter = settingOutlets.filter(
      outlet => outlet.tax_type === AFTER_DISCOUNT
    );

    const newOutletsBefore = [];
    for (const outlet of outletsBefore) {
      if (outlet.tax_type === AFTER_DISCOUNT) {
        outlet.tax_type = BEFORE_DISCOUNT;
        outlet.gratuity_type = BEFORE_DISCOUNT;
      }
      newOutletsBefore.push(outlet);
    }

    const newOutletsAfter = [];
    for (const outlet of outletsAfter) {
      if (outlet.tax_type === BEFORE_DISCOUNT) {
        outlet.tax_type = AFTER_DISCOUNT;
        outlet.gratuity_type = AFTER_DISCOUNT;
      }
      newOutletsAfter.push(outlet);
    }

    const totalOutlets = {
      previousOutletsBeforeDiscount: settingOutletsBefore.length,
      previousOutletsAfterDiscount: settingOutletsAfter.length,
      newOutletsBeforeDiscount: outletsBefore.length,
      newOutletsAfterDiscount: outletsAfter.length,
    };

    const assignedOutlets = outletsBefore.concat(outletsAfter);

    onConfirm(assignedOutlets);
    confirmTaxConfigurationClevertap(totalOutlets);
  };

  handleArrowRightClick = () => {
    const { selectedOutletsBefore, outletsAfter, outletsBefore } = this.state;
    const newOutletsBefore = outletsBefore.filter(
      outlet => !selectedOutletsBefore.includes(outlet)
    );
    const newOutletsAfter = outletsAfter.concat(selectedOutletsBefore);

    this.setState({
      outletsBefore: newOutletsBefore,
      outletsAfter: sortBy(newOutletsAfter, ['outlet_name']),
      selectedOutletsBefore: [],
      selectedOutletsAfter: selectedOutletsBefore,
    });

    setupTaxConfigurationClevertap();
  };

  handleArrowLeftClick = () => {
    const { selectedOutletsAfter, outletsAfter, outletsBefore } = this.state;
    const newOutletsAfter = outletsAfter.filter(
      outlet => !selectedOutletsAfter.includes(outlet)
    );
    const newOutletsBefore = outletsBefore.concat(selectedOutletsAfter);

    this.setState({
      outletsAfter: newOutletsAfter,
      outletsBefore: sortBy(newOutletsBefore, ['outlet_name']),
      selectedOutletsAfter: [],
      selectedOutletsBefore: selectedOutletsAfter,
    });

    setupTaxConfigurationClevertap();
  };

  getControlClickSelectedOutlets = (selectedOutlets = [], outletClicked) => {
    if (selectedOutlets.includes(outletClicked)) {
      return selectedOutlets.filter(
        selectedOutlet => selectedOutlet !== outletClicked
      );
    }
    return [...selectedOutlets].concat(outletClicked);
  };

  // Parent should not know shift key is pressed. Should be handled by AssignedOutletsSection
  // Tried to move some states but have some issues, so make it to do
  handleClickOutletBefore = (
    outlet,
    indexClicked,
    { shiftPressed = false, ctrlPressed = false }
  ) => {
    const {
      outletsBefore,
      lastClickedOutletIndexBefore,
      selectedOutletsBefore,
    } = this.state;
    let selectedOutlets = [];

    if (shiftPressed && ctrlPressed) {
      return;
    }
    if (shiftPressed) {
      if (lastClickedOutletIndexBefore === undefined) {
        selectedOutlets.push(outlet);
        this.setState({ lastClickedOutletIndexBefore: indexClicked });
      }

      const iterateFrom = Math.min(indexClicked, lastClickedOutletIndexBefore);
      const iterateTo = Math.max(indexClicked, lastClickedOutletIndexBefore);
      for (let i = iterateFrom; i <= iterateTo; i++) {
        selectedOutlets.push(outletsBefore[i]);
      }
    } else if (ctrlPressed) {
      selectedOutlets = this.getControlClickSelectedOutlets(
        selectedOutletsBefore,
        outlet
      );
    } else {
      selectedOutlets.push(outlet);
      this.setState({ lastClickedOutletIndexBefore: indexClicked });
    }

    this.setState({
      selectedOutletsBefore: selectedOutlets,
      selectedOutletsAfter: [],
    });
  };

  handleClickOutletAfter = (
    outlet,
    indexClicked,
    { shiftPressed = false, ctrlPressed = false }
  ) => {
    const {
      outletsAfter,
      lastClickedOutletIndexAfter,
      selectedOutletsAfter,
    } = this.state;
    let selectedOutlets = [];

    if (shiftPressed && ctrlPressed) {
      return;
    }
    if (shiftPressed) {
      if (lastClickedOutletIndexAfter === undefined) {
        selectedOutlets.push(outlet);
        this.setState({ lastClickedOutletIndexAfter: indexClicked });
      }

      const iterateFrom = Math.min(indexClicked, lastClickedOutletIndexAfter);
      const iterateTo = Math.max(indexClicked, lastClickedOutletIndexAfter);
      for (let i = iterateFrom; i <= iterateTo; i++) {
        selectedOutlets.push(outletsAfter[i]);
      }
    } else if (ctrlPressed) {
      selectedOutlets = this.getControlClickSelectedOutlets(
        selectedOutletsAfter,
        outlet
      );
    } else {
      selectedOutlets.push(outlet);
      this.setState({ lastClickedOutletIndexAfter: indexClicked });
    }

    this.setState({
      selectedOutletsAfter: selectedOutlets,
      selectedOutletsBefore: [],
    });
  };

  handleNameFilterBeforeChange = term => {
    this.setState({
      nameFilterBefore: term,
    });
  };

  handleNameFilterAfterChange = term => {
    this.setState({
      nameFilterAfter: term,
    });
  };
}

TaxConfigurationModal.defaultProps = {
  onCancel: () => {},
  onConfirm: () => {},
  outlets: [],
  settingOutlets: [],
};

export default TaxConfigurationModal;
